import { useEffect, useState } from 'react';
import { FormikProvider } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { PresetHeading } from '../../_partials/PresetHeading';
import { PreTransformForm } from './_partials/PreTransformForm';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import {
  getSilverPreTransformInitialValues,
  setSilverPreTransformFormValues,
} from '@/lib/datasource';
import { PreTransformSchema } from '@/zod/schemas/dataSource/PreTransform';
import { TwoColumnLayout } from '@/components/container';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { PreTransformDataPreviewContainer } from '@/components/dataSources/dataPreviewContainer/preTransform/PreTransformDataPreviewContainer';
import { useDebounceValidation } from '@/hooks/formik/useDebounceValidation';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

function DataSourceSilverPreTransformContent() {
  const {
    dataSource,
    handleNextStep,
    handleReview,
    save,
    handlePreviousStep,
    setFormik,
  } = useConfigDataSource();
  const { enableDatasourcePreview } = useFlags<FlagSet>();
  const [submitAction, setSubmitAction] =
    useState<DatasourceSubmitType>('next');

  const formik = useFormikWithAsyncValidation({
    initialValues: getSilverPreTransformInitialValues(dataSource),
    validate: toFormikValidate(PreTransformSchema),
    validationSchema: toFormikValidationSchema(PreTransformSchema),
    validateOnChange: false,
    onSubmit: async (newValues) => {
      const newDataSource = setSilverPreTransformFormValues(
        dataSource,
        newValues,
      );
      const result = await save(newDataSource);

      if (!result) {
        return;
      }

      // go to next step
      if (submitAction === 'next') {
        handleNextStep();
      } else if (submitAction === 'review') {
        handleReview();
      } else if (submitAction === 'previous') {
        handlePreviousStep();
      }
    },
  });

  useEffect(() => {
    setFormik({ handleSubmit: formik.handleSubmit, dirty: formik.dirty });
  }, [formik.handleSubmit, formik.dirty, setFormik]);

  useDebounceValidation(formik);

  return (
    <FormikProvider value={formik}>
      <TwoColumnLayout.Root>
        <TwoColumnLayout.LeftColumn>
          <div className="relative">
            <PresetHeading />
            <PreTransformForm
              submitAction={submitAction}
              setSubmitAction={setSubmitAction}
              submitDisabled={formik.submitDisabled}
              data-testid="pre-silver-transform-form"
            />
            <div className="absolute right-0 top-0">
              {enableDatasourcePreview && <PreTransformDataPreviewContainer />}
            </div>
          </div>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn> </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </FormikProvider>
  );
}

export default function DataSourceSilverPreTransform() {
  return (
    <AsyncValidationProvider>
      <DataSourceSilverPreTransformContent />
    </AsyncValidationProvider>
  );
}
